import { ServiceTemplates } from "../../components/proposals/ServiceTemplates";
import {
  SettingsSaveBar,
  SettingsSection,
} from "../../components/proposals/ProposalSettingsUI";
import PresentationSettingsEditor from "../../components/proposals/PresentationSettingsEditor";
import DealSettingsPanel from "./DealSettingsPanel";
/**
 * ProposalSettingsPage - Configure defaults for each proposal type
 * Default proposal text, featured manufacturer, and optional upgrades live on
 * the page; the Good/Better/Best tier templates are edited in the
 * TierTemplatesDrawer opened from the tab row.
 */

import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Checkbox,
  Box,
  Typography,
  Card,
  Button,
  IconButton,
  Stack,
  Input,
  Textarea,
  Sheet,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Select,
  Option,
  Switch,
} from "@mui/joy";
import {
  ArrowLeft as ArrowBack,
  Plus as Add,
  Trash as Delete,
  House as Home,
  Buildings as Business,
  Wrench as Build,
  ChartBar,
  Sparkle as AutoAwesome,
  CurrencyDollar as AttachMoney,
  Tag,
  ArrowUp,
  ArrowDown,
} from "@phosphor-icons/react";
import { proposalsApi } from "../../lib/proposalsApi";
import DefaultPricingTab from "../../components/proposals/DefaultPricingTab";
import TierNamesTab from "../../components/proposals/TierNamesTab";
import TierTemplatesDrawer from "../../components/proposals/TierTemplatesDrawer";
import { useTierLabels } from "../../hooks/useTierLabels";
import { apiClient } from "../../lib/api";
import { useIsMobile } from "../../hooks/useIsMobile";
import type {
  ProposalTemplate,
  ProposalTemplateDefaultTier,
  ProposalTemplateDefaultUpgrade,
  ProposalType,
  TierLabelMap,
} from "../../types/proposals";

type SettingsTabValue =
  | ProposalType
  | "default_pricing"
  | "tier_names"
  | "deal_workflows"
  | "services";

const PROPOSAL_TYPES: {
  value: ProposalType;
  label: string;
  icon: React.ReactNode;
  description: string;
}[] = [
  {
    value: "residential",
    label: "Residential",
    icon: <Home />,
    description: "Residential roofing projects",
  },
  {
    value: "commercial",
    label: "Commercial",
    icon: <Business />,
    description: "Commercial roofing projects",
  },
  {
    value: "other_projects",
    label: "Legacy project defaults",
    icon: <Build />,
    description: "Defaults for the original Other Projects proposal type",
  },
];

const SETTINGS_TABS: {
  value: SettingsTabValue;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "residential", label: "Residential", icon: <Home /> },
  { value: "commercial", label: "Commercial", icon: <Business /> },
  { value: "services", label: "Services & Templates", icon: <Build /> },
  { value: "default_pricing", label: "Default Pricing", icon: <AttachMoney /> },
  { value: "tier_names", label: "Tier Names", icon: <Tag /> },
  { value: "deal_workflows", label: "Insurance & Repair", icon: <Build /> },
];

// Default tier templates when creating new template. Seeded with whatever this
// org calls its tiers rather than a hardcoded Good/Better/Best.
const getDefaultTiers = (
  tierLabels: TierLabelMap,
): Omit<ProposalTemplateDefaultTier, "id">[] => [
  {
    tier_type: "good",
    name: tierLabels.good,
    description: "",
    advantages: [],
    tax_rate: 0,
    is_recommended: false,
    sort_order: 0,
    default_line_items: [],
  },
  {
    tier_type: "better",
    name: tierLabels.better,
    description: "",
    advantages: [],
    tax_rate: 0,
    is_recommended: true,
    sort_order: 1,
    default_line_items: [],
  },
  {
    tier_type: "best",
    name: tierLabels.best,
    description: "",
    advantages: [],
    tax_rate: 0,
    is_recommended: false,
    sort_order: 2,
    default_line_items: [],
  },
];

// Manufacturer type for dropdown
interface ManufacturerOption {
  id: number;
  name: string;
  logo: string | null;
}

export default function ProposalSettingsPage() {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<SettingsTabValue>(
    [...SETTINGS_TABS, ...PROPOSAL_TYPES].some(
      (tab) => tab.value === searchParams.get("tab"),
    )
      ? (searchParams.get("tab") as SettingsTabValue)
      : "residential",
  );
  const [editingTemplate, setEditingTemplate] =
    useState<ProposalTemplate | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [childDirty, setChildDirty] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = unsavedChanges;
  const switchTab = (value: SettingsTabValue) => {
    if (
      (unsavedChanges || childDirty) &&
      !window.confirm("You have unsaved changes. Discard them?")
    )
      return;
    setUnsavedChanges(false);
    setChildDirty(false);
    setSelectedTab(value);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("tab", value);
        return next;
      },
      { replace: true },
    );
  };
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (unsavedChanges || childDirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsavedChanges, childDirty]);
  const [tierDrawerOpen, setTierDrawerOpen] = useState(false);

  // Check if current tab is a proposal type tab (not one of the org-wide tabs)
  const isProposalTypeTab = PROPOSAL_TYPES.some((t) => t.value === selectedTab);
  const selectedType = isProposalTypeTab
    ? (selectedTab as ProposalType)
    : "residential";
  const selectedTypeLabel =
    PROPOSAL_TYPES.find((t) => t.value === selectedType)?.label ||
    "Residential";
  const { labels: tierLabels } = useTierLabels();

  // Fetch all templates (only needed for template tabs, not default_pricing)
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["proposal-templates"],
    queryFn: () => proposalsApi.listTemplates(),
    enabled: isProposalTypeTab,
  });

  // Fetch manufacturers for dropdown
  const { data: manufacturers = [] } = useQuery<ManufacturerOption[]>({
    queryKey: ["manufacturers"],
    queryFn: async () => {
      const response = await apiClient.get<any>("/api/manufacturers/");
      return response.data?.results || response.data || [];
    },
  });

  // Get template for selected type
  const currentTemplate = templatesData?.results?.find(
    (t) => t.proposal_type === selectedType,
  );

  // Initialize editing state when template changes (only for proposal type tabs)
  useEffect(() => {
    if (!isProposalTypeTab) {
      // Don't manage template state when on default_pricing tab
      return;
    }
    if (dirtyRef.current) return;
    if (currentTemplate) {
      setEditingTemplate({
        ...currentTemplate,
        default_upgrades: [...(currentTemplate.default_upgrades || [])].sort(
          (a, b) => a.sort_order - b.sort_order,
        ),
      });
    } else {
      // Create a new template structure for this type
      const typeConfig = PROPOSAL_TYPES.find((t) => t.value === selectedType);
      setEditingTemplate({
        id: 0, // Will be created on save
        proposal_type: selectedType,
        proposal_type_display: typeConfig?.label || selectedType,
        name: typeConfig?.label || selectedType,
        description: "",
        default_introduction_text: "",
        default_terms_and_conditions: "",
        default_featured_manufacturer: null,
        default_featured_manufacturer_details: null,
        show_total_squares: false,
        is_active: true,
        sort_order: 0,
        default_tiers: getDefaultTiers(
          tierLabels,
        ) as ProposalTemplateDefaultTier[],
        default_upgrades: [],
        created_at: "",
        updated_at: "",
      });
    }
    setUnsavedChanges(false);
  }, [currentTemplate, selectedType, isProposalTypeTab, tierLabels]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (template: ProposalTemplate) => {
      const templateData = {
        presentation_options: template.presentation_options || {},
        name: template.name,
        description: template.description,
        default_introduction_text: template.default_introduction_text,
        default_terms_and_conditions: template.default_terms_and_conditions,
        default_featured_manufacturer: template.default_featured_manufacturer,
        show_total_squares: template.show_total_squares,
        is_active: template.is_active,
        default_tiers: template.default_tiers.map((tier) => ({
          tier_type: tier.tier_type,
          name: tier.name,
          description: tier.description,
          advantages: tier.advantages,
          tax_rate: tier.tax_rate,
          is_recommended: tier.is_recommended,
          sort_order: tier.sort_order,
          default_line_items: tier.default_line_items.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unit_price: item.unit_price,
            sort_order: item.sort_order,
          })),
        })),
        default_upgrades: (template.default_upgrades || []).map(
          (upgrade, index) => ({
            name: upgrade.name,
            description: upgrade.description,
            quantity: upgrade.quantity,
            unit: upgrade.unit,
            unit_price: upgrade.unit_price,
            is_required: upgrade.is_required ?? false,
            sort_order: index,
          }),
        ),
      };

      if (template.id) {
        return proposalsApi.updateTemplate(template.id, templateData);
      } else {
        return proposalsApi.createTemplate({
          proposal_type: template.proposal_type,
          ...templateData,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      toast.success("Template saved successfully");
      setUnsavedChanges(false);
    },
    onError: () => {
      toast.error("Failed to save template");
    },
  });

  const moveUpgrade = (index: number, direction: -1 | 1) => {
    if (!editingTemplate) return;
    const upgrades = [...(editingTemplate.default_upgrades || [])];
    const destination = index + direction;
    if (destination < 0 || destination >= upgrades.length) return;
    [upgrades[index], upgrades[destination]] = [
      upgrades[destination],
      upgrades[index],
    ];
    setEditingTemplate({
      ...editingTemplate,
      default_upgrades: upgrades.map((upgrade, sort_order) => ({
        ...upgrade,
        sort_order,
      })),
    });
    setUnsavedChanges(true);
  };

  const upgradeOrderControls = (
    upgrade: ProposalTemplateDefaultUpgrade,
    index: number,
  ) => (
    <Stack direction="row" spacing={0.25} alignItems="center">
      <IconButton
        size="sm"
        variant="plain"
        aria-label={`Move ${upgrade.name || "upgrade"} up`}
        title="Move up"
        disabled={index === 0 || saveMutation.isPending}
        onClick={() => moveUpgrade(index, -1)}
      >
        <ArrowUp size={18} />
      </IconButton>
      <IconButton
        size="sm"
        variant="plain"
        aria-label={`Move ${upgrade.name || "upgrade"} down`}
        title="Move down"
        disabled={
          index === (editingTemplate?.default_upgrades?.length || 0) - 1 ||
          saveMutation.isPending
        }
        onClick={() => moveUpgrade(index, 1)}
      >
        <ArrowDown size={18} />
      </IconButton>
    </Stack>
  );

  // Load defaults mutation
  const loadDefaultsMutation = useMutation({
    mutationFn: () => proposalsApi.loadDefaultTemplates(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      if (data.created > 0) {
        toast.success(
          `Created ${data.created} default template(s) with pre-configured line items`,
        );
      } else {
        toast.success("All templates already exist. No changes made.");
      }
    },
    onError: () => {
      toast.error("Failed to load default templates");
    },
  });

  const handleSave = () => {
    if (editingTemplate) {
      saveMutation.mutate(editingTemplate);
    }
  };

  // Persist tier changes coming back from the drawer immediately
  const handleSaveTiers = (tiers: ProposalTemplateDefaultTier[]) => {
    if (!editingTemplate) return;
    const merged = { ...editingTemplate, default_tiers: tiers };
    setEditingTemplate(merged);
    saveMutation.mutate(merged, { onSuccess: () => setTierDrawerOpen(false) });
  };

  if (isLoading && isProposalTypeTab) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 3, alignItems: { xs: "stretch", sm: "center" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <IconButton
            component={Link}
            to="/crm/proposals"
            variant="outlined"
            size="sm"
            sx={{ borderRadius: "50%" }}
            aria-label="Back to proposals"
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography level="h3">Proposal Settings</Typography>
            <Typography level="body-md" sx={{ color: "text.secondary" }}>
              Set up reusable templates, prices, and customer-facing content.
            </Typography>
          </Box>
        </Box>
      </Stack>

      {/* Settings Tabs */}
      <Tabs
        value={selectedTab === "other_projects" ? "services" : selectedTab}
        onChange={(_, value) => switchTab(value as SettingsTabValue)}
        sx={{ bgcolor: "transparent" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <TabList
            disableUnderline
            sx={{
              p: 0.5,
              gap: 0.5,
              borderRadius: "xl",
              bgcolor: "background.level1",
              flexWrap: "wrap",
            }}
          >
            {SETTINGS_TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                disableIndicator
                sx={{
                  borderRadius: "lg",
                  flex: { xs: "1 1 45%", sm: "0 0 auto" },
                  fontSize: { xs: "sm", sm: "md" },
                  px: { xs: 1.25, sm: 2 },
                  whiteSpace: "nowrap",
                  '&[aria-selected="true"]': {
                    bgcolor: "background.surface",
                    boxShadow: "sm",
                    color: "primary.plainColor",
                    fontWeight: "lg",
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {tab.icon}
                  <span>{tab.label}</span>
                </Stack>
              </Tab>
            ))}
          </TabList>
        </Box>

        {/* Default Pricing Tab */}
        {selectedTab !== "other_projects" && (
          <TabPanel value="services" sx={{ p: 0, pt: 1 }}>
            <ServiceTemplates
              onLegacyDefaults={() => switchTab("other_projects")}
              onDirtyChange={setChildDirty}
            />
          </TabPanel>
        )}
        <TabPanel value="deal_workflows" sx={{ p: 0, pt: 1 }}>
          <DealSettingsPanel onDirtyChange={setChildDirty} />
        </TabPanel>
        <TabPanel value="default_pricing" sx={{ p: 0, pt: 1 }}>
          <DefaultPricingTab onDirtyChange={setChildDirty} />
        </TabPanel>

        {/* Tier Names Tab */}
        <TabPanel value="tier_names" sx={{ p: 0, pt: 1 }}>
          <TierNamesTab onDirtyChange={setChildDirty} />
        </TabPanel>

        {/* Proposal Type Tabs */}
        {PROPOSAL_TYPES.filter((type) => type.value === selectedTab).map(
          (type) => (
            <TabPanel
              key={type.value}
              value={type.value === "other_projects" ? "services" : type.value}
              hidden={selectedTab !== type.value}
              sx={{ p: 0, pt: 1 }}
            >
              {type.value === "other_projects" && (
                <Button
                  variant="plain"
                  startDecorator={<ArrowBack />}
                  onClick={() => switchTab("services")}
                  sx={{ mb: 2 }}
                >
                  Back to Services & Templates
                </Button>
              )}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={2}
                justifyContent="space-between"
                alignItems={{ sm: "center" }}
                sx={{ mb: 3 }}
              >
                <Box>
                  <Typography level="h4">
                    {type.label}
                    {type.value !== "other_projects" && " defaults"}
                  </Typography>
                  <Typography level="body-sm" textColor="text.secondary">
                    Starting content for new{" "}
                    {type.value === "other_projects"
                      ? "Other Projects"
                      : type.label.toLowerCase()}{" "}
                    proposals. Customize individual proposals as needed.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startDecorator={<ChartBar />}
                  onClick={() => setTierDrawerOpen(true)}
                >
                  Edit package templates
                </Button>
              </Stack>

              {editingTemplate && (
                <>
                  <Stack spacing={2}>
                    <SettingsSection
                      title="Proposal text"
                      description="Your introduction letter and terms & conditions."
                    >
                      <Stack spacing={3}>
                        <FormControl>
                          <FormLabel>Default Introduction Letter</FormLabel>
                          <Textarea
                            minRows={4}
                            value={
                              editingTemplate.default_introduction_text || ""
                            }
                            onChange={(e) => {
                              setEditingTemplate({
                                ...editingTemplate,
                                default_introduction_text: e.target.value,
                              });
                              setUnsavedChanges(true);
                            }}
                            placeholder="Dear customer, thank you for considering us for your project..."
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel>Default Terms & Conditions</FormLabel>
                          <Textarea
                            minRows={6}
                            value={
                              editingTemplate.default_terms_and_conditions || ""
                            }
                            onChange={(e) => {
                              setEditingTemplate({
                                ...editingTemplate,
                                default_terms_and_conditions: e.target.value,
                              });
                              setUnsavedChanges(true);
                            }}
                            placeholder="Enter your default terms and conditions..."
                          />
                        </FormControl>
                      </Stack>
                    </SettingsSection>
                    <SettingsSection
                      title="Manufacturer & measurements"
                      description="Choose featured marketing materials and whether to show roof squares."
                    >
                      <Stack spacing={3}>
                        <FormControl>
                          <Typography
                            level="body-xs"
                            sx={{ color: "text.secondary", mb: 1 }}
                          >
                            New proposals will automatically use this
                            manufacturer's marketing materials
                          </Typography>
                          <Select
                            placeholder="Select manufacturer..."
                            value={
                              editingTemplate.default_featured_manufacturer
                            }
                            onChange={(_, value) => {
                              setEditingTemplate({
                                ...editingTemplate,
                                default_featured_manufacturer: value as
                                  | number
                                  | null,
                              });
                              setUnsavedChanges(true);
                            }}
                          >
                            <Option value={null}>None</Option>
                            {manufacturers.map((mfr) => (
                              <Option key={mfr.id} value={mfr.id}>
                                {mfr.name}
                              </Option>
                            ))}
                          </Select>
                        </FormControl>

                        <Divider />

                        <FormControl
                          orientation="horizontal"
                          sx={{ justifyContent: "space-between", gap: 2 }}
                        >
                          <Box>
                            <FormLabel>Show Total Squares on Pricing</FormLabel>
                            <Typography
                              level="body-xs"
                              sx={{ color: "text.secondary" }}
                            >
                              Display the total square count below the price on
                              each pricing tier
                            </Typography>
                          </Box>
                          <Switch
                            checked={
                              editingTemplate.show_total_squares || false
                            }
                            onChange={(e) => {
                              setEditingTemplate({
                                ...editingTemplate,
                                show_total_squares: e.target.checked,
                              });
                              setUnsavedChanges(true);
                            }}
                          />
                        </FormControl>
                      </Stack>
                    </SettingsSection>
                    <SettingsSection
                      title="Default Add-ons & Fees"
                      description={`${editingTemplate.default_upgrades?.length || 0} items · required charges and optional customer upgrades.`}
                    >
                      <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary", mb: 2 }}
                      >
                        Required charges are included in every package; optional
                        upgrades can be selected by customers. Use the arrows to
                        set their order, then save changes. New proposals will
                        show upgrades in this order.
                      </Typography>

                      {(editingTemplate.default_upgrades || []).length > 0 ? (
                        isMobile ? (
                          <Stack spacing={1.5} sx={{ mb: 2 }}>
                            {(editingTemplate.default_upgrades || []).map(
                              (upgrade, index) => (
                                <Card
                                  key={upgrade.id ?? index}
                                  variant="outlined"
                                  sx={{ p: 2, gap: 1.5 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      level="title-sm"
                                      sx={{ flex: 1, minWidth: 0 }}
                                    >
                                      {upgrade.name || "Upgrade"}
                                    </Typography>
                                    {upgradeOrderControls(upgrade, index)}
                                    <IconButton
                                      size="sm"
                                      variant="plain"
                                      color="danger"
                                      aria-label={`Delete ${upgrade.name || "upgrade"}`}
                                      onClick={() => {
                                        const newUpgrades = (
                                          editingTemplate.default_upgrades || []
                                        ).filter((_, i) => i !== index);
                                        setEditingTemplate({
                                          ...editingTemplate,
                                          default_upgrades: newUpgrades,
                                        });
                                        setUnsavedChanges(true);
                                      }}
                                    >
                                      <Delete size={18} />
                                    </IconButton>
                                  </Box>
                                  <Checkbox
                                    label="Required charge"
                                    checked={!!upgrade.is_required}
                                    onChange={(e) => {
                                      const items = [
                                        ...(editingTemplate.default_upgrades ||
                                          []),
                                      ];
                                      items[index] = {
                                        ...items[index],
                                        is_required: e.target.checked,
                                      };
                                      setEditingTemplate({
                                        ...editingTemplate,
                                        default_upgrades: items,
                                      });
                                      setUnsavedChanges(true);
                                    }}
                                  />
                                  <FormControl size="sm">
                                    <FormLabel>Name</FormLabel>
                                    <Input
                                      value={upgrade.name}
                                      onChange={(e) => {
                                        const newUpgrades = [
                                          ...(editingTemplate.default_upgrades ||
                                            []),
                                        ];
                                        newUpgrades[index] = {
                                          ...newUpgrades[index],
                                          name: e.target.value,
                                        };
                                        setEditingTemplate({
                                          ...editingTemplate,
                                          default_upgrades: newUpgrades,
                                        });
                                        setUnsavedChanges(true);
                                      }}
                                      placeholder="Upgrade name"
                                    />
                                  </FormControl>
                                  <FormControl size="sm">
                                    <FormLabel>Description</FormLabel>
                                    <Input
                                      value={upgrade.description}
                                      onChange={(e) => {
                                        const newUpgrades = [
                                          ...(editingTemplate.default_upgrades ||
                                            []),
                                        ];
                                        newUpgrades[index] = {
                                          ...newUpgrades[index],
                                          description: e.target.value,
                                        };
                                        setEditingTemplate({
                                          ...editingTemplate,
                                          default_upgrades: newUpgrades,
                                        });
                                        setUnsavedChanges(true);
                                      }}
                                      placeholder="Description"
                                    />
                                  </FormControl>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      "& > *": { minWidth: 0 },
                                      "& input": { minWidth: 0 },
                                    }}
                                  >
                                    <FormControl size="sm" sx={{ flex: 1 }}>
                                      <FormLabel>Qty</FormLabel>
                                      <Input
                                        type="number"
                                        value={upgrade.quantity}
                                        onChange={(e) => {
                                          const newUpgrades = [
                                            ...(editingTemplate.default_upgrades ||
                                              []),
                                          ];
                                          newUpgrades[index] = {
                                            ...newUpgrades[index],
                                            quantity:
                                              parseFloat(e.target.value) || 0,
                                          };
                                          setEditingTemplate({
                                            ...editingTemplate,
                                            default_upgrades: newUpgrades,
                                          });
                                          setUnsavedChanges(true);
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl size="sm" sx={{ flex: 1 }}>
                                      <FormLabel>Unit</FormLabel>
                                      <Input
                                        value={upgrade.unit}
                                        onChange={(e) => {
                                          const newUpgrades = [
                                            ...(editingTemplate.default_upgrades ||
                                              []),
                                          ];
                                          newUpgrades[index] = {
                                            ...newUpgrades[index],
                                            unit: e.target.value,
                                          };
                                          setEditingTemplate({
                                            ...editingTemplate,
                                            default_upgrades: newUpgrades,
                                          });
                                          setUnsavedChanges(true);
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl size="sm" sx={{ flex: 1 }}>
                                      <FormLabel>Unit Price</FormLabel>
                                      <Input
                                        type="number"
                                        startDecorator="$"
                                        value={upgrade.unit_price}
                                        onChange={(e) => {
                                          const newUpgrades = [
                                            ...(editingTemplate.default_upgrades ||
                                              []),
                                          ];
                                          newUpgrades[index] = {
                                            ...newUpgrades[index],
                                            unit_price:
                                              parseFloat(e.target.value) || 0,
                                          };
                                          setEditingTemplate({
                                            ...editingTemplate,
                                            default_upgrades: newUpgrades,
                                          });
                                          setUnsavedChanges(true);
                                        }}
                                      />
                                    </FormControl>
                                  </Box>
                                </Card>
                              ),
                            )}
                          </Stack>
                        ) : (
                          <Sheet
                            variant="outlined"
                            sx={{
                              borderRadius: "sm",
                              overflow: "hidden",
                              mb: 2,
                            }}
                          >
                            <Box sx={{ overflowX: "auto" }}>
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor:
                                        "var(--joy-palette-background-level1)",
                                    }}
                                  >
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "left",
                                        fontSize: "12px",
                                        width: "80px",
                                      }}
                                    >
                                      Order
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "left",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Name
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "left",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Description
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "center",
                                        fontSize: "12px",
                                        width: "70px",
                                      }}
                                    >
                                      Qty
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "center",
                                        fontSize: "12px",
                                        width: "70px",
                                      }}
                                    >
                                      Unit
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        textAlign: "right",
                                        fontSize: "12px",
                                        width: "100px",
                                      }}
                                    >
                                      Unit Price
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 12px",
                                        width: "50px",
                                      }}
                                    ></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(editingTemplate.default_upgrades || []).map(
                                    (upgrade, index) => (
                                      <tr
                                        key={upgrade.id ?? index}
                                        style={{
                                          borderTop:
                                            "1px solid var(--joy-palette-divider)",
                                        }}
                                      >
                                        <td style={{ padding: "4px 8px" }}>
                                          {upgradeOrderControls(upgrade, index)}
                                        </td>
                                        <td style={{ padding: "4px 8px" }}>
                                          <Checkbox
                                            label="Required charge"
                                            checked={!!upgrade.is_required}
                                            onChange={(e) => {
                                              const items = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              items[index] = {
                                                ...items[index],
                                                is_required: e.target.checked,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: items,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                          />
                                          <Input
                                            size="sm"
                                            variant="plain"
                                            value={upgrade.name}
                                            onChange={(e) => {
                                              const newUpgrades = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              newUpgrades[index] = {
                                                ...newUpgrades[index],
                                                name: e.target.value,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                            placeholder="Upgrade name"
                                            sx={{
                                              "--Input-focusedHighlight":
                                                "transparent",
                                            }}
                                          />
                                        </td>
                                        <td style={{ padding: "4px 8px" }}>
                                          <Input
                                            size="sm"
                                            variant="plain"
                                            value={upgrade.description}
                                            onChange={(e) => {
                                              const newUpgrades = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              newUpgrades[index] = {
                                                ...newUpgrades[index],
                                                description: e.target.value,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                            placeholder="Description"
                                            sx={{
                                              "--Input-focusedHighlight":
                                                "transparent",
                                            }}
                                          />
                                        </td>
                                        <td style={{ padding: "4px 8px" }}>
                                          <Input
                                            size="sm"
                                            variant="plain"
                                            type="number"
                                            value={upgrade.quantity}
                                            onChange={(e) => {
                                              const newUpgrades = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              newUpgrades[index] = {
                                                ...newUpgrades[index],
                                                quantity:
                                                  parseFloat(e.target.value) ||
                                                  0,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                            sx={{
                                              textAlign: "center",
                                              "--Input-focusedHighlight":
                                                "transparent",
                                            }}
                                          />
                                        </td>
                                        <td style={{ padding: "4px 8px" }}>
                                          <Input
                                            size="sm"
                                            variant="plain"
                                            value={upgrade.unit}
                                            onChange={(e) => {
                                              const newUpgrades = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              newUpgrades[index] = {
                                                ...newUpgrades[index],
                                                unit: e.target.value,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                            sx={{
                                              textAlign: "center",
                                              "--Input-focusedHighlight":
                                                "transparent",
                                            }}
                                          />
                                        </td>
                                        <td style={{ padding: "4px 8px" }}>
                                          <Input
                                            size="sm"
                                            variant="plain"
                                            type="number"
                                            value={upgrade.unit_price}
                                            onChange={(e) => {
                                              const newUpgrades = [
                                                ...(editingTemplate.default_upgrades ||
                                                  []),
                                              ];
                                              newUpgrades[index] = {
                                                ...newUpgrades[index],
                                                unit_price:
                                                  parseFloat(e.target.value) ||
                                                  0,
                                              };
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                            startDecorator="$"
                                            sx={{
                                              textAlign: "right",
                                              "--Input-focusedHighlight":
                                                "transparent",
                                            }}
                                          />
                                        </td>
                                        <td
                                          style={{
                                            padding: "4px 8px",
                                            textAlign: "center",
                                          }}
                                        >
                                          <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="danger"
                                            aria-label={`Delete ${upgrade.name || "upgrade"}`}
                                            onClick={() => {
                                              const newUpgrades = (
                                                editingTemplate.default_upgrades ||
                                                []
                                              ).filter((_, i) => i !== index);
                                              setEditingTemplate({
                                                ...editingTemplate,
                                                default_upgrades: newUpgrades,
                                              });
                                              setUnsavedChanges(true);
                                            }}
                                          >
                                            <Delete size={18} />
                                          </IconButton>
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </Box>
                          </Sheet>
                        )
                      ) : (
                        <Typography
                          level="body-sm"
                          sx={{
                            color: "text.tertiary",
                            textAlign: "center",
                            py: 2,
                          }}
                        >
                          No add-ons or fees yet. Add a required charge or an
                          optional customer upgrade.
                        </Typography>
                      )}

                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        startDecorator={<Add />}
                        onClick={() => {
                          const newUpgrade: ProposalTemplateDefaultUpgrade = {
                            // Temporary identity keeps focus with this row while reordering before save.
                            id:
                              Math.min(
                                0,
                                ...(editingTemplate.default_upgrades || []).map(
                                  (upgrade) => upgrade.id || 0,
                                ),
                              ) - 1,
                            name: "",
                            description: "",
                            quantity: 1,
                            unit: "ea",
                            unit_price: 0,
                            sort_order: (editingTemplate.default_upgrades || [])
                              .length,
                          };
                          setEditingTemplate({
                            ...editingTemplate,
                            default_upgrades: [
                              ...(editingTemplate.default_upgrades || []),
                              newUpgrade,
                            ],
                          });
                          setUnsavedChanges(true);
                        }}
                      >
                        Add add-on or fee
                      </Button>
                    </SettingsSection>
                    <SettingsSection
                      title="Presentation"
                      description="Page order, customer experience, colors, and display preferences."
                    >
                      <PresentationSettingsEditor
                        value={editingTemplate.presentation_options || {}}
                        onChange={(value) => {
                          setEditingTemplate({
                            ...editingTemplate,
                            presentation_options: value,
                          });
                          setUnsavedChanges(true);
                        }}
                      />
                    </SettingsSection>
                    <SettingsSection
                      title="Starter defaults"
                      description="Add missing starter templates without replacing your saved settings."
                    >
                      <Button
                        variant="outlined"
                        startDecorator={<AutoAwesome />}
                        loading={loadDefaultsMutation.isPending}
                        onClick={() => loadDefaultsMutation.mutate()}
                      >
                        Load Default Templates
                      </Button>
                    </SettingsSection>
                  </Stack>
                  <SettingsSaveBar
                    dirty={unsavedChanges}
                    saving={saveMutation.isPending}
                    onSave={handleSave}
                    label="Save Changes"
                  >
                    <Typography level="body-sm" textColor="text.secondary">
                      Applies to new proposals using these defaults.
                    </Typography>
                  </SettingsSaveBar>
                </>
              )}
            </TabPanel>
          ),
        )}
      </Tabs>

      {/* Tier Templates drawer */}
      <TierTemplatesDrawer
        open={tierDrawerOpen}
        onClose={() => setTierDrawerOpen(false)}
        proposalTypeLabel={selectedTypeLabel}
        tiers={editingTemplate?.default_tiers || []}
        saving={saveMutation.isPending}
        onSave={handleSaveTiers}
      />
    </Box>
  );
}
